import React from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import Image from '@atlaskit/image';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { Images } from '@atlassian/smart-knowledge-cards/images';
import { ImagesSkeleton } from '@atlassian/smart-knowledge-cards/images-skeleton';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { useImages } from '../api/useImages/useImages';
import emptyStateSpotDark from '../assets/visual-knowledge-card-empty-dark.svg';
import emptyStateSpotLight from '../assets/visual-knowledge-card-empty-light.svg';
import errorStateSpotDark from '../assets/visual-knowledge-card-error-dark.svg';
import errorStateSpotLight from '../assets/visual-knowledge-card-error-light.svg';

const messages = defineMessages({
	heading: {
		id: 'search_ai.visual_knowledge_card.heading',
		defaultMessage: 'Related images',
	},
	loadingLabel: {
		id: 'search_ai.visual_knowledge_card.loading_label',
		defaultMessage: 'Loading related images',
	},
	statusBodyText: {
		id: 'search_ai.visual_knowledge_card.body_text',
		defaultMessage: 'Try searching again.',
	},
	errorTitle: {
		id: 'search_ai.visual_knowledge_card.error_title',
		defaultMessage: "Something went wrong and we couldn't find related images.",
	},
	emptyTitle: {
		id: 'search_ai.visual_knowledge_card.empty_title',
		defaultMessage: "We couldn't find any images for this search.",
	},
});

const styles = cssMap({
	stateContainer: {
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.large'),
		paddingTop: token('space.100'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.200'),
	},
	title: {
		color: token('color.text.subtlest'),
		font: token('font.body'),
	},
	body: {
		color: token('color.text.subtlest'),
		font: token('font.body.small'),
	},
});

const VisualKnowledgeCardStatusMessage = ({
	heading,
	imageSrc,
	imageSrcDark,
	title,
	body,
}: {
	heading: string;
	imageSrc: string;
	imageSrcDark: string;
	title: string;
	body: string;
}): React.JSX.Element => (
	<Stack space="space.100">
		<Heading as="h2" size="xsmall">{heading}</Heading>
		<Box xcss={styles.stateContainer}>
			<Inline alignBlock="center" space="space.200">
				<Image src={imageSrc} srcDark={imageSrcDark} width={72} height={72} alt="" />
				<Stack space="space.050">
					<Box xcss={styles.title}>{title}</Box>
					<Box xcss={styles.body}>{body}</Box>
				</Stack>
			</Inline>
		</Box>
	</Stack>
);

export const VisualKnowledgeCard = (): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { commonAttributes, sourceProduct } = useAIAnswerContext();
	const { cloudId, workspaceId, query } = commonAttributes;

	const { images, loading, error } = useImages({
		searchQuery: query,
		cloudId,
		workspaceId,
		product: sourceProduct ?? '',
	});

	if (loading) {
		return (
			<ImagesSkeleton
				heading={formatMessage(messages.heading)}
				aria-label={formatMessage(messages.loadingLabel)}
			/>
		);
	}

	if (error) {
		return (
			<VisualKnowledgeCardStatusMessage
				heading={formatMessage(messages.heading)}
				imageSrc={errorStateSpotLight}
				imageSrcDark={errorStateSpotDark}
				title={formatMessage(messages.errorTitle)}
				body={formatMessage(messages.statusBodyText)}
			/>
		);
	}

	if (!images || images.length === 0) {
		return (
			<VisualKnowledgeCardStatusMessage
				heading={formatMessage(messages.heading)}
				imageSrc={emptyStateSpotLight}
				imageSrcDark={emptyStateSpotDark}
				title={formatMessage(messages.emptyTitle)}
				body={formatMessage(messages.statusBodyText)}
			/>
		);
	}

	const mappedImages = images.map((image) => ({
		altText: image.altText,
		imageUrl: image.thumbnailUrl,
		sourceTitle: image.title,
		sourceUrl: image.url,
	}));

	return <Images heading={formatMessage(messages.heading)} images={mappedImages} />;
};
